// #40 Hours-Log Phase-1 — weekly work-task draft view.
//
// A PSU-style, output-only surface that renders the timesheet draft the
// agent compiles from the briefing/state (design: docs/plans/40-hours-log-
// design.md). Two sections:
//   §A — 4-week activity breakout: per week, the work items with agent-known
//        activity, grouped Category → Item, each with an attribution label.
//   §B — known-but-no-activity: open items quiet across the whole 4-week
//        window (the memory-jog for off-record work).
//
// Attribution names are SCALE TEAM ONLY (the people who log hours) — confirmed
// (the owner) renders plain, inferred renders "possibly <name>". External
// senders never appear. Phase 1 is read-only; hours input is Phase 2.
//
// Data source: GET /hours-log (session-authenticated; reads the snapshot the
// agent pushed via PUT /admin/hours-log/:slug on the last weekly run).

import {
  ApiError, downloadFile, getJson, postJson, putJson, wireDownloadControl,
} from "./api";

export interface Attribution {
  confirmed: string[];
  possibly: string[];
}

export interface HoursLogAction {
  ai_slug: string | null;
  display_number: string | null;
  text: string | null;
  narrative: string | null;
  priority_status: string;
  // Per-action "worked by" (may be empty → defers to the Item owner).
  attribution?: Attribution;
}

export interface HoursLogItem {
  slug: string;
  display_number: string | null;
  name: string;
  // #89: phase band (Phase → Category → Item), mirrors the briefing.
  phase?: string | null;
  thread_name: string | null;
  thread_display_number: string | null;
  narrative: string | null;
  is_new?: boolean;
  // #109b: the Item's purpose-built "worked-this-week" narrative (the synth's
  // Item.hours_log_narrative), "more verbal, less list form" — it replaces the
  // #89 semicolon roll-up of Action titles (kept as the fallback for older
  // snapshots). Same field name, so the render below is unchanged.
  // `actions`/`attribution` are retained for the #91 per-person hours track.
  actions_summary?: string;
  attribution: Attribution;
  actions: HoursLogAction[];
}

export interface HoursLogWeek {
  week_start: string;
  week_end: string;
  items: HoursLogItem[];
}

export interface HoursLogNoActivityItem {
  slug: string;
  display_number: string | null;
  name: string;
  phase?: string | null;
  thread_name: string | null;
  thread_display_number: string | null;
  narrative: string | null;
  // #40c: real most-recent week with activity (week_end), derived from an
  // extended briefing lookback. null = dormant beyond the lookback. Prefer this
  // over `last_active`, which is week_end-stamped on every upsert (unreliable).
  last_touched?: string | null;
  last_active: string | null;
  attribution: Attribution;
}

export interface HoursLogSnapshot {
  project_slug: string;
  snapshot_at: string | null;
  weeks: HoursLogWeek[];
  no_activity: HoursLogNoActivityItem[];
  counts?: {
    weeks: number;
    active_items: number;
    no_activity_items: number;
  };
}

export async function fetchHoursLog(): Promise<HoursLogSnapshot> {
  return getJson<HoursLogSnapshot>("/hours-log");
}

// --- §7 Hours-Log Phase-2 — self-reported hours entry ------------------------
//
// A roster member (the signed-in recipient, joined to the project `team`
// server-side) enters their OWN hours per Item, plus employee-week misc
// buckets and add-a-row. Off-roster recipients (board members) see the
// read-only Phase-1 view unchanged. Design: 40-hours-log-design.md §7.

export interface MiscBuckets {
  office_admin: number;
  non_billable: number;
  pto: number;
}
const MISC_BUCKETS: (keyof MiscBuckets)[] = ["office_admin", "non_billable", "pto"];
const MISC_LABELS: Record<keyof MiscBuckets, string> = {
  office_admin: "Office / Admin",
  non_billable: "Non-billable",
  pto: "PTO",
};

// §7 #2 — the per-week section holding self-added work (custom "not listed"
// rows + logged open-items). One source of truth for the SPA; the Excel uses
// the same wording. Operator to finalize the wording (2026-07-23).
export const ALSO_WORKED_LABEL = "Also worked this week";

export interface HoursEntryRowInput {
  row_key: string;
  label: string;
  hours: number;
  note?: string | null;
}

export interface HoursEntriesMeWeek {
  week_end: string;
  rows: HoursEntryRowInput[];
  misc: MiscBuckets | null;
  updated_at: string | null;
}

export interface HoursEntriesMe {
  can_enter: boolean;
  display_name: string | null;
  weeks: HoursEntriesMeWeek[];
}

export async function fetchHoursEntriesMe(): Promise<HoursEntriesMe> {
  return getJson<HoursEntriesMe>("/hours-entries/me");
}

export interface SaveHoursResult {
  ok: boolean;
  updated_at: string;
}

export async function saveHoursEntries(
  weekEnd: string, rows: HoursEntryRowInput[], misc: MiscBuckets,
): Promise<SaveHoursResult> {
  return putJson<SaveHoursResult>("/hours-entries", {
    week_end: weekEnd, rows, misc,
  });
}

// Round to the quarter-hour grid the Worker enforces (0.25 steps). A blank /
// non-numeric field reads as 0. Clamped to [0, 168].
export function normalizeHours(raw: string | number | null | undefined): number {
  const n = typeof raw === "number" ? raw : parseFloat(String(raw ?? "").trim());
  if (!isFinite(n) || n <= 0) return 0;
  return Math.min(168, Math.round(n * 4) / 4);
}

// One editable row's working value: the denormalized label travels with it so
// a later slug merge/close can't orphan the billing entry (§7b).
interface WorkingRow {
  label: string;
  hours: number;
}

// The per-week working copy the SPA mutates before Save. `item:` rows for the
// week's §A items and any §B items logged in place live here keyed by row_key;
// custom rows carry a `custom:` key.
export class WeekEntryModel {
  readonly weekEnd: string;
  private rows = new Map<string, WorkingRow>();
  misc: MiscBuckets = { office_admin: 0, non_billable: 0, pto: 0 };
  private savedSig: string;
  private customSeq = 0;

  constructor(seed?: HoursEntriesMeWeek) {
    this.weekEnd = seed?.week_end ?? "";
    for (const r of seed?.rows ?? []) {
      this.rows.set(r.row_key, {
        label: r.label, hours: normalizeHours(r.hours),
      });
    }
    if (seed?.misc) {
      for (const b of MISC_BUCKETS) this.misc[b] = normalizeHours(seed.misc[b]);
    }
    // Seed the custom-key counter past any seeded custom rows so a fresh
    // add-a-row can't collide with a key restored from /me (which would
    // silently overwrite an existing entry).
    const prefix = `custom:${this.weekEnd}-`;
    for (const key of this.rows.keys()) {
      if (key.startsWith(prefix)) {
        const n = Number(key.slice(prefix.length));
        if (Number.isInteger(n) && n >= this.customSeq) this.customSeq = n + 1;
      }
    }
    this.savedSig = this.signature();
  }

  getHours(rowKey: string): number {
    return this.rows.get(rowKey)?.hours ?? 0;
  }

  setHours(rowKey: string, label: string, hours: number): void {
    const h = normalizeHours(hours);
    if (h <= 0) this.rows.delete(rowKey);
    else this.rows.set(rowKey, { label, hours: h });
  }

  setMisc(bucket: keyof MiscBuckets, hours: number): void {
    this.misc[bucket] = normalizeHours(hours);
  }

  // Rename a still-present row (an editable custom / add-a-row label). No-op if
  // the row was dropped (hours went to 0) — a label with no hours never saves.
  setLabel(rowKey: string, label: string): void {
    const r = this.rows.get(rowKey);
    if (r) r.label = label;
  }

  // A custom add-a-row starts empty; the row_key is minted client-side and
  // is stable for this session so repeated edits target the same entry.
  addCustomRow(label: string, hours: number): string {
    const key = `custom:${this.weekEnd}-${this.customSeq++}`;
    this.setHours(key, label, hours);
    return key;
  }

  // The committed custom (add-a-row) entries — re-rendered on every repaint /
  // reload so they survive a filter change (they persist in the model, but the
  // add-a-row host is rebuilt each render). Order is insertion order.
  customRows(): Array<{ rowKey: string; label: string }> {
    const out: Array<{ rowKey: string; label: string }> = [];
    for (const [rowKey, r] of this.rows) {
      if (rowKey.startsWith("custom:")) out.push({ rowKey, label: r.label });
    }
    return out;
  }

  total(): number {
    let t = 0;
    for (const r of this.rows.values()) t += r.hours;
    for (const b of MISC_BUCKETS) t += this.misc[b];
    return Math.round(t * 4) / 4;
  }

  // The PUT payload: only rows with positive hours (a 0-hour item is the
  // default — no need to persist it). Sorted for a stable signature/diff.
  serializeRows(): HoursEntryRowInput[] {
    return [...this.rows.entries()]
      .filter(([, r]) => r.hours > 0)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([row_key, r]) => ({ row_key, label: r.label, hours: r.hours }));
  }

  private signature(): string {
    return JSON.stringify([this.serializeRows(), this.misc]);
  }

  isDirty(): boolean {
    return this.signature() !== this.savedSig;
  }

  markSaved(): void {
    this.savedSig = this.signature();
  }
}

// Ties the per-week models to the snapshot + save transport. §B (no-activity)
// rows have no week of their own, so they attach to the MOST-RECENT displayed
// week's document (§7d + operator 2026-07-23) — one Save flushes both.
export class HoursEntryController {
  readonly canEnter: boolean;
  readonly displayName: string | null;
  private byWeek = new Map<string, WeekEntryModel>();
  private order: string[] = [];
  private saveFn: typeof saveHoursEntries;

  constructor(
    snapshot: HoursLogSnapshot,
    me: HoursEntriesMe | null,
    saveFn: typeof saveHoursEntries = saveHoursEntries,
  ) {
    this.canEnter = Boolean(me?.can_enter);
    this.displayName = me?.display_name ?? null;
    this.saveFn = saveFn;
    const seedByWeek = new Map<string, HoursEntriesMeWeek>();
    for (const w of me?.weeks ?? []) seedByWeek.set(w.week_end, w);
    for (const w of snapshot.weeks) {
      this.order.push(w.week_end);
      this.byWeek.set(w.week_end, new WeekEntryModel(seedByWeek.get(w.week_end)));
    }
  }

  week(weekEnd: string): WeekEntryModel | undefined {
    return this.byWeek.get(weekEnd);
  }

  // The document §B rows flush into (weeks are snapshot-ordered, newest first).
  mostRecentWeek(): string | null {
    return this.order[0] ?? null;
  }

  async save(weekEnd: string): Promise<void> {
    const model = this.byWeek.get(weekEnd);
    if (!model) return;
    await this.saveFn(weekEnd, model.serializeRows(), model.misc);
    model.markSaved();
  }

  // §B rows can target any displayed week, so the §B Save flushes EVERY dirty
  // week (not just the most-recent one). Returns the count saved.
  async saveAllDirty(): Promise<number> {
    const dirty = [...this.byWeek.entries()].filter(([, m]) => m.isDirty());
    for (const [weekEnd] of dirty) await this.save(weekEnd);
    return dirty.length;
  }

  anyDirty(): boolean {
    for (const m of this.byWeek.values()) if (m.isDirty()) return true;
    return false;
  }

  // §B placement (operator 2026-07-23): a no-activity item with hours logged in
  // a week surfaces under THAT week's card — and, because the same item can be
  // logged across several weeks, it appears under EVERY week it has hours in
  // (not just the first). It also stays in the bottom §B list regardless, so a
  // recipient can add it to another week. Returns week_end → items with hours
  // there.
  quietItemsByWeek(
    weekEnds: string[], items: HoursLogNoActivityItem[],
  ): Map<string, HoursLogNoActivityItem[]> {
    const byWeek = new Map<string, HoursLogNoActivityItem[]>();
    for (const we of weekEnds) {
      const here = items.filter(
        (it) => (this.byWeek.get(we)?.getHours(`item:${it.slug}`) ?? 0) > 0,
      );
      if (here.length) byWeek.set(we, here);
    }
    return byWeek;
  }
}

// --- Generate-and-archive (PSU-style) ---------------------------------------

export interface HoursLogGenerationListItem {
  id: string;
  generated_at: string;
  generated_by: string;
  filename: string;
  label: string | null | undefined;
}

export interface HoursLogGenerationsResponse {
  generations: HoursLogGenerationListItem[];
}

export async function fetchHoursLogGenerations(): Promise<HoursLogGenerationsResponse> {
  return getJson<HoursLogGenerationsResponse>("/hours-log-generations");
}

export interface HoursLogGenerationCreateResponse {
  id: string;
  generated_at: string;
  filename: string;
  label: string | null | undefined;
}

export type WeekScope = "all" | "recent" | "month-current" | "month-last";

const _MONTHS = ["January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December"];
// "YYYY-MM" month key from an ISO date (UTC, avoids TZ drift on a bare date).
function monthKeyOf(iso: string): string {
  return (iso || "").slice(0, 7);
}
function prevMonthKey(key: string): string {
  const [y, m] = key.split("-").map(Number);
  if (!y || !m) return key;
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, "0")}`;
}
function monthName(key: string): string {
  const m = Number(key.split("-")[1]);
  return (m >= 1 && m <= 12 ? _MONTHS[m - 1] : undefined) ?? key;
}

export async function createHoursLogGeneration(
  scope: WeekScope, label: string | null = null,
): Promise<HoursLogGenerationCreateResponse> {
  // Filename is left to the Worker (it picks the agent-computed name for the
  // scope); we only send the on-screen filter scope + an optional label.
  return postJson<HoursLogGenerationCreateResponse>("/hours-log-generations", {
    scope, label,
  });
}

// A 409 stale-regenerating means the Worker fired a re-render (§7 freshness):
// the consolidated .xlsx was stale relative to just-saved hours. The agent
// workflow re-renders + re-pushes in ~30-60s, so we poll Generate until it
// succeeds (or the budget runs out — then the caller surfaces a "try again"
// message; a later Generate will pick up the fresh doc).
function isStaleRegenerating(e: unknown): boolean {
  return e instanceof ApiError && e.status === 409
    && typeof e.body === "object" && e.body !== null
    && (e.body as { error?: string }).error === "stale-regenerating";
}

export interface GenerateOptions {
  retries?: number;
  delayMs?: number;
  onRegenerating?: () => void;
  sleep?: (ms: number) => Promise<void>;  // injectable for tests
  generate?: (
    scope: WeekScope, label: string | null,
  ) => Promise<HoursLogGenerationCreateResponse>;  // injectable for tests
}

export async function generateHoursLogWithRefresh(
  scope: WeekScope, label: string | null, opts: GenerateOptions = {},
): Promise<HoursLogGenerationCreateResponse> {
  // The regen is a GitHub Actions round-trip (~1-2 min incl. cold start). Poll
  // every 3s for up to ~2.5 min so a single Generate click waits it out and
  // returns the moment the fresh doc is ready — rather than giving up early and
  // asking the recipient to click again. (Option 3, operator 2026-07-23.)
  const retries = opts.retries ?? 50;
  const delayMs = opts.delayMs ?? 3000;
  const sleep = opts.sleep ?? ((ms: number) => new Promise((r) => setTimeout(r, ms)));
  const generate = opts.generate ?? createHoursLogGeneration;
  let announced = false;
  for (let attempt = 0; ; attempt++) {
    try {
      return await generate(scope, label);
    } catch (e) {
      if (isStaleRegenerating(e) && attempt < retries) {
        if (!announced) { opts.onRegenerating?.(); announced = true; }
        await sleep(delayMs);
        continue;
      }
      throw e;
    }
  }
}

export function hoursLogGenerationDownloadUrl(id: string): string {
  return `/hours-log-generations/${encodeURIComponent(id)}/download`;
}

function formatGeneratedAt(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso || "");
  if (!m) return iso || "";
  return `${isoToShort(`${m[1]}-${m[2]}-${m[3]}`)} ${m[1]} ${m[4]}:${m[5]}`;
}

// Render the Hours-Log left rail: Generate button at the top, then the
// Generated Logs archive list (PSU pattern). `onGenerated` refreshes the view.
export function renderHoursLogRail(
  rail: HTMLElement,
  generations: HoursLogGenerationListItem[],
  getScope: () => WeekScope,
  onGenerated: () => void | Promise<void>,
): void {
  rail.innerHTML = "";

  const genCard = document.createElement("aside");
  genCard.className = "sidebar hours-log-generate-card";
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "hours-log-generate-btn";
  btn.textContent = "Generate Hours Log";
  // Explanation on hover rather than inline, to keep the rail clean.
  btn.title = "Generates the on-screen action list as an Excel timesheet (matches the week filter).";
  const err = document.createElement("p");
  err.className = "hours-log-generate-error";
  err.hidden = true;
  // Shown only when a save-triggered regen is in flight (the doc was stale) —
  // the recipient is told the wait is expected and bounded (operator 2026-07-23).
  const waitNote = document.createElement("p");
  waitNote.className = "hours-log-generate-note";
  waitNote.hidden = true;
  waitNote.textContent =
    "Compiling your latest hours into the file — this can take up to 2 minutes. "
    + "It'll download on its own when it's ready.";
  btn.addEventListener("click", async () => {
    btn.disabled = true;
    btn.textContent = "Generating…";
    err.hidden = true;
    waitNote.hidden = true;
    try {
      // Honor the on-screen week filter at generate time. §7 freshness: if the
      // consolidated doc is stale relative to just-saved hours, the Worker 409s
      // + re-renders; generateHoursLogWithRefresh polls until it's fresh.
      const result = await generateHoursLogWithRefresh(getScope(), null, {
        onRegenerating: () => {
          btn.textContent = "Refreshing latest hours…";
          waitNote.hidden = false;  // regen in flight → explain the wait
        },
      });
      // Auto-download the freshly archived .xlsx via the authenticated fetch
      // layer (downloadFile) — a bare anchor can't send X-Project-Slug and 400s
      // post-#94 (see api.ts). Decoupled from onGenerated() so a download error
      // doesn't hide the archived row or read as "Generate failed" (the row's
      // Download link is the fallback). Mirrors the PSU Generate flow.
      waitNote.hidden = true;  // ready → the file downloads now
      try {
        await downloadFile(
          hoursLogGenerationDownloadUrl(result.id), result.filename,
        );
      } catch {
        // Generation succeeded; only the auto-download failed — non-fatal.
      }
      await onGenerated();
    } catch (e) {
      btn.disabled = false;
      btn.textContent = "Generate Hours Log";
      waitNote.hidden = true;
      err.hidden = false;
      if (e instanceof ApiError && e.status === 409) {
        // The re-render was still running when the retry budget ran out — the
        // dispatch is in flight, so a Generate in ~a minute will be fresh.
        err.textContent =
          "Still refreshing the latest hours — try Generate again in a moment.";
      } else if (e instanceof Error && /404/.test(e.message)) {
        err.textContent = "No timesheet rendered yet — it's refreshed each weekly run.";
      } else {
        err.textContent = `Generate failed: ${e instanceof Error ? e.message : String(e)}`;
      }
    }
  });
  genCard.appendChild(btn);
  genCard.appendChild(waitNote);
  genCard.appendChild(err);
  rail.appendChild(genCard);

  if (generations.length > 0) {
    const listCard = document.createElement("aside");
    listCard.className = "sidebar hours-log-generations-sidebar";
    const h = document.createElement("h2");
    h.textContent = `Generated Logs (${generations.length})`;
    listCard.appendChild(h);
    const list = document.createElement("ul");
    list.className = "kil-generations-list";
    for (const gen of generations) {
      const li = document.createElement("li");
      li.className = "kil-generation-row";
      const fname = document.createElement("span");
      fname.className = "kil-generation-filename";
      fname.textContent = gen.filename;
      fname.title = `Generated ${formatGeneratedAt(gen.generated_at)} by ${gen.generated_by}`;
      li.appendChild(fname);
      const dl = document.createElement("a");
      dl.className = "kil-generation-download";
      // Download through the authenticated fetch layer, NOT a bare href — a
      // bare-anchor GET can't send X-Project-Slug and 400s post-#94 (api.ts).
      dl.href = "#";
      dl.setAttribute("role", "button");
      dl.title = `Download ${gen.filename}`;
      wireDownloadControl(
        dl, hoursLogGenerationDownloadUrl(gen.id), gen.filename, (msg) => {
          let errEl = li.querySelector<HTMLElement>(".hours-log-download-error");
          if (!errEl) {
            errEl = document.createElement("div");
            errEl.className = "hours-log-download-error hours-log-generate-error";
            errEl.hidden = false;
            li.appendChild(errEl);
          }
          errEl.textContent = msg;
        });
      dl.innerHTML =
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" ' +
        'stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
        'stroke-linejoin="round" aria-hidden="true">' +
        '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>' +
        '<polyline points="7 10 12 15 17 10"/>' +
        '<line x1="12" y1="15" x2="12" y2="3"/></svg>';
      li.appendChild(dl);
      list.appendChild(li);
    }
    listCard.appendChild(list);
    rail.appendChild(listCard);
  }
}

// "Ray, Marcus, possibly Andrea" — confirmed first (plain), possibly last
// (each prefixed). Empty → "" (caller renders a dash).
export function formatAttribution(attr: Attribution | null | undefined): string {
  if (!attr) return "";
  const parts = [...(attr.confirmed ?? [])];
  for (const n of attr.possibly ?? []) parts.push(`possibly ${n}`);
  return parts.join(", ");
}

function fmtWeekRange(start: string, end: string): string {
  // "Jun 15 – Jun 21, 2026" — compact, locale-stable.
  const s = isoToShort(start);
  const e = isoToShort(end);
  const yr = (end || "").slice(0, 4);
  return s && e ? `${s} – ${e}${yr ? `, ${yr}` : ""}` : end;
}

function isoToShort(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  if (!m) return iso || "";
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const mo = months[Number(m[2]) - 1] ?? m[2];
  return `${mo} ${Number(m[3])}`;
}

// --- Renderer ---------------------------------------------------------------

// Entry-mode UI context threaded through the renderers. `refreshAll` repaints
// every registered control's live total + dirty/Save state after any edit
// (N ≤ 4 weeks, so a blanket refresh is cheap). §A and §B both re-render
// together on a filter change, so `refreshers` is fully volatile — reset at
// the top of each repaint before the sections re-register.
interface EntryUI {
  controller: HoursEntryController;
  refreshers: Array<() => void>;
  refreshAll: () => void;
  // Full re-render of §A + §B — used when a §B item's placement changes (it
  // gains/loses hours and must hop between the no-activity list and its week).
  repaint: () => void;
}

export function renderHoursLog(
  root: HTMLElement,
  snapshot: HoursLogSnapshot,
  scopeRef: { value: WeekScope } = { value: "all" },
  controller: HoursEntryController | null = null,
): void {
  root.innerHTML = "";
  const main = document.createElement("main");
  main.className = "hours-log-view";

  // Entry mode is on only for a roster member (server-verified via /me).
  // Off-roster recipients get the Phase-1 read-only view unchanged.
  const ui: EntryUI | null = controller?.canEnter
    ? { controller, refreshers: [], refreshAll: () => {}, repaint: () => {} }
    : null;
  if (ui) ui.refreshAll = () => { for (const fn of ui.refreshers) fn(); };

  // (b) Header row — title left, the "Show" filter right-justified.
  const header = document.createElement("div");
  header.className = "hours-log-header";
  const heading = document.createElement("h2");
  heading.textContent = "Hours Log";
  header.appendChild(heading);
  main.appendChild(header);

  const subtitle = document.createElement("p");
  subtitle.className = "hours-log-subtitle";
  if (ui) {
    const who = ui.controller.displayName ? ` as ${ui.controller.displayName}` : "";
    subtitle.textContent =
      `Enter your hours${who} on what you worked, then Save each week. ` +
      "Cells left blank count as no time.";
  } else if (snapshot.snapshot_at) {
    subtitle.textContent =
      "A draft of what was worked on, week by week — to jog memory before " +
      `logging hours. Snapshot ${
        isoToShort(snapshot.snapshot_at)} ${snapshot.snapshot_at.slice(0, 4)}.`;
  } else {
    subtitle.textContent = "Nothing compiled yet — the next weekly run will populate this.";
  }
  main.appendChild(subtitle);

  const hasWeeks = snapshot.weeks.some((w) => w.items.length > 0);
  if (!hasWeeks && snapshot.no_activity.length === 0) {
    const empty = document.createElement("p");
    empty.className = "hours-log-empty";
    empty.textContent = "No activity recorded in the recent window.";
    main.appendChild(empty);
    root.appendChild(main);
    return;
  }

  // (c) Month scopes anchored to the latest week in the snapshot (deterministic,
  // not the wall clock) — current + previous calendar month, dynamically named.
  const latest = snapshot.weeks[0]?.week_end ?? snapshot.snapshot_at ?? "";
  const cur = monthKeyOf(latest);
  const last = prevMonthKey(cur);

  // --- Filter bar (right-justified in the header). Selection is shared
  // (scopeRef) so the Generate button honors it too.
  const label = document.createElement("label");
  label.className = "hours-log-filter";
  const labelText = document.createElement("span");
  labelText.textContent = "Date Range";
  const select = document.createElement("select");
  const opts: [WeekScope, string][] = [
    ["recent", "Most recent week"],
    ["all", "Last 4 weeks"],
    ["month-current", `This month (${monthName(cur)})`],
    ["month-last", `Last month (${monthName(last)})`],
  ];
  for (const [value, text] of opts) {
    const o = document.createElement("option");
    o.value = value;
    o.textContent = text;
    if (value === scopeRef.value) o.selected = true;
    select.appendChild(o);
  }
  label.appendChild(labelText);
  label.appendChild(select);
  header.appendChild(label);

  // --- §A — activity breakout + §B — no-activity. Both re-render together on a
  // filter change so §B's per-row week dropdown always offers the weeks in view.
  const sectionA = document.createElement("section");
  sectionA.className = "hours-log-section hours-log-activity";
  main.appendChild(sectionA);
  const sectionB = snapshot.no_activity.length > 0
    ? (() => {
        const s = document.createElement("section");
        s.className = "hours-log-section hours-log-no-activity";
        main.appendChild(s);
        return s;
      })()
    : null;

  const filteredWeeks = (): HoursLogWeek[] => {
    if (scopeRef.value === "recent") return snapshot.weeks.slice(0, 1);
    if (scopeRef.value === "all") return snapshot.weeks.slice(0, 4);
    if (scopeRef.value === "month-current")
      return snapshot.weeks.filter((w) => monthKeyOf(w.week_end) === cur);
    if (scopeRef.value === "month-last")
      return snapshot.weeks.filter((w) => monthKeyOf(w.week_end) === last);
    return snapshot.weeks;
  };

  const repaint = (): void => {
    const weeks = filteredWeeks();
    // Both sections re-render, so every refresher is stale — reset before the
    // fresh cards/rows re-register (avoids closures pointing at detached DOM).
    if (ui) ui.refreshers = [];

    // §B placement (operator 2026-07-23): a logged no-activity item surfaces
    // under every week it has hours in (multi-week), while ALSO staying in the
    // bottom §B list so it can be added to another week.
    const placedByWeek = ui
      ? ui.controller.quietItemsByWeek(
          snapshot.weeks.map((w) => w.week_end), snapshot.no_activity,
        )
      : new Map<string, HoursLogNoActivityItem[]>();

    sectionA.innerHTML = "";
    if (weeks.length === 0) {
      const none = document.createElement("p");
      none.className = "hours-log-week-empty";
      none.textContent = "No tracked activity in this period.";
      sectionA.appendChild(none);
    } else {
      for (const week of weeks) {
        sectionA.appendChild(
          renderWeek(week, ui, placedByWeek.get(week.week_end) ?? []),
        );
      }
    }
    if (sectionB) {
      sectionB.innerHTML = "";
      // §B always lists ALL no-activity items (operator 2026-07-23): a logged
      // item stays here so it can be added to another week too.
      renderNoActivityInto(sectionB, snapshot.no_activity, ui, weeks);
    }
    if (ui) ui.refreshAll();
  };
  if (ui) ui.repaint = repaint;
  select.addEventListener("change", () => {
    scopeRef.value = select.value as WeekScope;
    repaint();
  });
  repaint();

  root.appendChild(main);
}

// One right-aligned quarter-hour input bound to a WeekEntryModel row. Blank =
// no time (0, which drops the row on serialize). `inputmode=decimal` gives a
// numeric phone keypad. Commits on input; a global refresh follows. `label`
// may be a thunk so an editable custom row's rename travels with the entry.
// `onZeroBlur` fires when the field blurs at 0 — used by a placed §B row to
// return to the no-activity list once its hours are cleared.
function makeHoursCell(
  model: WeekEntryModel, rowKey: string, label: string | (() => string), ui: EntryUI,
  opts: { onZeroBlur?: () => void } = {},
): HTMLElement {
  const labelOf = (): string => (typeof label === "function" ? label() : label);
  const input = document.createElement("input");
  input.className = "hours-log-cell";
  input.type = "text";
  input.inputMode = "decimal";
  input.setAttribute("aria-label", `Hours for ${labelOf()}`);
  const cur = model.getHours(rowKey);
  input.value = cur > 0 ? String(cur) : "";
  input.addEventListener("input", () => {
    model.setHours(rowKey, labelOf(), normalizeHours(input.value));
    ui.refreshAll();
  });
  // Normalize the display to the grid on blur (e.g. "1.3" → "1.25").
  input.addEventListener("blur", () => {
    const h = model.getHours(rowKey);
    input.value = h > 0 ? String(h) : "";
    if (h <= 0 && opts.onZeroBlur) opts.onZeroBlur();
  });
  return input;
}

// A quarter-hour hours cell paired with a bucket setter (used by misc rows).
function makeMiscCell(model: WeekEntryModel, bucket: keyof MiscBuckets, ui: EntryUI): HTMLElement {
  const input = document.createElement("input");
  input.className = "hours-log-cell";
  input.type = "text";
  input.inputMode = "decimal";
  input.setAttribute("aria-label", MISC_LABELS[bucket]);
  const cur = model.misc[bucket];
  input.value = cur > 0 ? String(cur) : "";
  input.addEventListener("input", () => {
    model.setMisc(bucket, normalizeHours(input.value));
    ui.refreshAll();
  });
  input.addEventListener("blur", () => {
    input.value = model.misc[bucket] > 0 ? String(model.misc[bucket]) : "";
  });
  return input;
}

// §7 "Also worked this week" section (operator 2026-07-23 #2): a labelled,
// category-styled block after the briefing items, separated by a thin line
// (like the non-project footer). Holds the placed "open, but no recent" items
// logged against this week (#4) + the add-a-row for work the briefing missed.
// At category indent (not nested under the last item). Custom labels editable.
function renderProjectExtras(
  weekEnd: string, ui: EntryUI, placedQuiet: HoursLogNoActivityItem[] = [],
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "hours-log-project-extras";
  const model = ui.controller.week(weekEnd);
  if (!model) return wrap;

  const head = document.createElement("div");
  head.className = "hours-log-alsoworked-head";
  head.textContent = ALSO_WORKED_LABEL;
  wrap.appendChild(head);

  // Placed §B items — rendered like a project row, tagged so it's clear they
  // came from the open-items list. Zeroing the hours drops it from this week
  // (it stays in the §B list below for logging against another week).
  for (const item of placedQuiet) {
    const row = document.createElement("div");
    row.className = "hours-log-entry-row hours-log-placed-row";
    const lbl = document.createElement("span");
    lbl.className = "hours-log-entry-label";
    lbl.textContent = item.name;
    const tag = document.createElement("span");
    tag.className = "hours-log-placed-tag";
    tag.textContent = "open item";
    lbl.appendChild(tag);
    row.appendChild(lbl);
    row.appendChild(makeHoursCell(model, `item:${item.slug}`, item.name, ui, {
      onZeroBlur: () => ui.repaint(),  // dropped from this week → re-render
    }));
    wrap.appendChild(row);
  }

  const customHost = document.createElement("div");
  customHost.className = "hours-log-custom-rows";
  // Re-render already-committed custom rows (survive a filter repaint / reload).
  for (const { rowKey, label } of model.customRows()) {
    customHost.appendChild(renderCustomRow(model, rowKey, label, ui));
  }
  wrap.appendChild(customHost);

  const addRow = document.createElement("div");
  addRow.className = "hours-log-entry-row hours-log-add-row";
  const addLabel = document.createElement("input");
  addLabel.className = "hours-log-add-label";
  addLabel.type = "text";
  addLabel.placeholder = "Worked on something not listed? Add a project row";
  addLabel.setAttribute("aria-label", "New project row description");
  const addHours = document.createElement("input");
  addHours.className = "hours-log-cell";
  addHours.type = "text";
  addHours.inputMode = "decimal";
  addHours.setAttribute("aria-label", "New project row hours");
  const commitAdd = (): void => {
    const label = addLabel.value.trim();
    const hours = normalizeHours(addHours.value);
    if (!label || hours <= 0) return;
    const key = model.addCustomRow(label, hours);
    customHost.appendChild(renderCustomRow(model, key, label, ui));
    addLabel.value = "";
    addHours.value = "";
    ui.refreshAll();
  };
  addHours.addEventListener("change", commitAdd);
  addRow.appendChild(addLabel);
  addRow.appendChild(addHours);
  wrap.appendChild(addRow);
  return wrap;
}

// The per-week entry footer: NON-PROJECT (misc) buckets + live total + Save.
// (operator #1) the "Non-project time" head sits at the phase/category indent,
// not indented under the item list. Registers a refresher so any edit in the
// week keeps the total + Save state current.
function renderEntryFooter(weekEnd: string, ui: EntryUI): HTMLElement {
  const model = ui.controller.week(weekEnd);
  const footer = document.createElement("div");
  footer.className = "hours-log-entry-footer";
  if (!model) return footer;

  const miscHead = document.createElement("div");
  miscHead.className = "hours-log-misc-head";
  miscHead.textContent = "Non-project time";
  footer.appendChild(miscHead);
  const miscRows = document.createElement("div");
  miscRows.className = "hours-log-misc-rows";
  for (const bucket of MISC_BUCKETS) {
    const row = document.createElement("div");
    row.className = "hours-log-entry-row";
    const lbl = document.createElement("span");
    lbl.className = "hours-log-entry-label";
    lbl.textContent = MISC_LABELS[bucket];
    row.appendChild(lbl);
    row.appendChild(makeMiscCell(model, bucket, ui));
    miscRows.appendChild(row);
  }
  footer.appendChild(miscRows);

  // Save + live total.
  const bar = document.createElement("div");
  bar.className = "hours-log-save-bar";
  const total = document.createElement("span");
  total.className = "hours-log-week-total";
  const dirtyDot = document.createElement("span");
  dirtyDot.className = "hours-log-dirty-dot";
  dirtyDot.hidden = true;
  const saveBtn = document.createElement("button");
  saveBtn.type = "button";
  saveBtn.className = "hours-log-save-btn";
  saveBtn.textContent = "Save week";
  const err = document.createElement("span");
  err.className = "hours-log-save-error";
  err.hidden = true;
  saveBtn.addEventListener("click", async () => {
    saveBtn.disabled = true;
    saveBtn.textContent = "Saving…";
    err.hidden = true;
    try {
      await ui.controller.save(weekEnd);
      // On success the model is clean → let refreshAll own the disabled state
      // (it disables a clean Save). Set the label first so the refresher's
      // "Saved"→"Save week" re-dirty guard keeps it reading "Saved".
      saveBtn.textContent = "Saved";
      ui.refreshAll();
    } catch (e) {
      err.hidden = false;
      err.textContent = e instanceof Error ? e.message : String(e);
      saveBtn.textContent = "Save week";
      saveBtn.disabled = false;  // re-enable only on failure, so a retry is possible
    }
  });
  bar.appendChild(total);
  bar.appendChild(dirtyDot);
  bar.appendChild(saveBtn);
  bar.appendChild(err);
  footer.appendChild(bar);

  ui.refreshers.push(() => {
    const t = model.total();
    total.textContent = `Your total: ${t} h`;
    const dirty = model.isDirty();
    dirtyDot.hidden = !dirty;
    saveBtn.disabled = !dirty;
    if (dirty && saveBtn.textContent === "Saved") saveBtn.textContent = "Save week";
  });
  return footer;
}

// A committed custom (add-a-row) entry — editable label (operator #3: click to
// rename) + hours cell.
function renderCustomRow(
  model: WeekEntryModel, rowKey: string, label: string, ui: EntryUI,
): HTMLElement {
  const row = document.createElement("div");
  row.className = "hours-log-entry-row hours-log-custom-row";
  const lbl = document.createElement("input");
  lbl.className = "hours-log-custom-label";
  lbl.type = "text";
  lbl.value = label;
  lbl.setAttribute("aria-label", "Row description");
  lbl.addEventListener("input", () => {
    // Keep the model label + the hours cell's aria in sync as it's renamed.
    model.setLabel(rowKey, lbl.value.trim() || label);
    ui.refreshAll();
  });
  row.appendChild(lbl);
  row.appendChild(makeHoursCell(model, rowKey, () => lbl.value.trim() || label, ui));
  return row;
}

// #89 — phase band labels/order (mirrors the briefing's #37 phase tier).
const PHASE_LABELS: Record<string, string> = {
  pre_development: "Pre-Development",
  pre_construction: "Pre-Construction",
  construction: "Construction",
  general: "General",
};
const PHASE_ORDER = ["pre_development", "pre_construction", "construction", "general"];
function phaseSortKey(p: string | null | undefined): number {
  const i = PHASE_ORDER.indexOf(p ?? "");
  return i < 0 ? PHASE_ORDER.length : i;
}

// Generic Phase → Category → Item grouping, shared by §A (activity) and §B
// (no-activity). Phase renders as a quiet eyebrow above the Category header
// (Category is the visual anchor — client 2026-07-01 (d)). Phase bands activate
// only when >=1 item carries a non-null, non-'general' phase.
interface Grouped { phase?: string | null; thread_name: string | null }
function appendPhaseCategoryGroups<T extends Grouped>(
  container: HTMLElement, items: T[], renderItem: (it: T) => HTMLElement,
): void {
  const renderCats = (group: T[]): HTMLElement => {
    const wrap = document.createElement("div");
    wrap.className = "hours-log-cats";
    const byCat = new Map<string, T[]>();
    for (const it of group) {
      const cat = it.thread_name ?? "Uncategorized";
      const arr = byCat.get(cat) ?? [];
      arr.push(it);
      byCat.set(cat, arr);
    }
    for (const [cat, catItems] of byCat) {
      const head = document.createElement("div");
      head.className = "hours-log-cat";
      head.textContent = cat;
      wrap.appendChild(head);
      const list = document.createElement("div");
      list.className = "hours-log-items";
      for (const it of catItems) list.appendChild(renderItem(it));
      wrap.appendChild(list);
    }
    return wrap;
  };

  const showPhases = items.some((it) => it.phase && it.phase !== "general");
  if (!showPhases) {
    container.appendChild(renderCats(items));
    return;
  }
  const byPhase = new Map<string | null | undefined, T[]>();
  for (const it of items) {
    const arr = byPhase.get(it.phase) ?? [];
    arr.push(it);
    byPhase.set(it.phase, arr);
  }
  const phases = [...byPhase.keys()].sort((a, b) => phaseSortKey(a) - phaseSortKey(b));
  for (const phase of phases) {
    const band = document.createElement("div");
    band.className = "hours-log-phase";
    band.textContent = PHASE_LABELS[phase ?? ""] ?? "General";
    container.appendChild(band);
    container.appendChild(renderCats(byPhase.get(phase) as T[]));
  }
}

function renderWeek(
  week: HoursLogWeek, ui: EntryUI | null = null,
  placedQuiet: HoursLogNoActivityItem[] = [],
): HTMLElement {
  const block = document.createElement("section");
  block.className = "hours-log-week";

  const h = document.createElement("h4");
  h.className = "hours-log-week-heading";
  h.textContent = fmtWeekRange(week.week_start, week.week_end);
  block.appendChild(h);

  const model = ui ? ui.controller.week(week.week_end) : undefined;
  if (week.items.length === 0 && placedQuiet.length === 0) {
    const none = document.createElement("p");
    none.className = "hours-log-week-empty";
    none.textContent = "No tracked activity this week.";
    block.appendChild(none);
    // A week with no §A items can still carry misc/PTO + add-a-row in entry mode.
    if (ui) {
      block.appendChild(renderProjectExtras(week.week_end, ui, placedQuiet));
      block.appendChild(renderEntryFooter(week.week_end, ui));
    }
    return block;
  }
  appendPhaseCategoryGroups(
    block, week.items,
    (it) => renderItem(it, ui && model ? { ui, model } : null),
  );
  if (ui) {
    // Project-flow rows (operator #2): items you logged from the open-items
    // list (placedQuiet) + custom add-a-rows, at category indent; then the
    // non-project (misc) footer + Save + total below.
    block.appendChild(renderProjectExtras(week.week_end, ui, placedQuiet));
    block.appendChild(renderEntryFooter(week.week_end, ui));
  }
  return block;
}

interface ItemEntryCtx { ui: EntryUI; model: WeekEntryModel }

function renderItem(item: HoursLogItem, entry: ItemEntryCtx | null = null): HTMLElement {
  const card = document.createElement("article");
  card.className = "hours-log-item";
  card.dataset.slug = item.slug;

  // #89: Item name only (Category/Phase are grouping headers above) — no owner
  // column (a #91 concern). Below it, ONE condensed line: the action titles
  // joined by "; " (the timesheet roll-up, not a per-action breakout).
  // §7 Phase-2: in entry mode the title row gains a right-aligned hours cell.
  const title = document.createElement("div");
  title.className = "hours-log-item-title";
  title.textContent = item.name;
  if (entry) {
    card.classList.add("hours-log-item-entry");
    const titleRow = document.createElement("div");
    titleRow.className = "hours-log-item-titlerow";
    titleRow.appendChild(title);
    titleRow.appendChild(
      makeHoursCell(entry.model, `item:${item.slug}`, item.name, entry.ui),
    );
    card.appendChild(titleRow);
  } else {
    card.appendChild(title);
  }

  const summary =
    item.actions_summary ??
    item.actions.map((a) => a.text).filter(Boolean).join("; ");
  if (summary) {
    const line = document.createElement("div");
    line.className = "hours-log-item-summary";
    line.textContent = summary;
    card.appendChild(line);
  }

  return card;
}

// §B — known-but-no-activity, rendered into an existing container (re-run on
// filter change so the per-row week dropdown offers the weeks currently in
// view). `inViewWeeks` are the weeks §A is showing.
function renderNoActivityInto(
  section: HTMLElement,
  items: HoursLogNoActivityItem[],
  ui: EntryUI | null,
  inViewWeeks: HoursLogWeek[],
): void {
  const h = document.createElement("h3");
  h.className = "hours-log-section-heading";
  h.textContent = "Open, but no recent activity";
  section.appendChild(h);

  const note = document.createElement("p");
  note.className = "hours-log-subtitle";
  note.textContent = ui
    ? (inViewWeeks.length > 1
        ? "Worked one of these off the record? Enter hours and pick the week — "
          + "it moves up into that week."
        : "Worked one of these off the record? Enter hours — it moves up into "
          + "the week above.")
    : "Still open but with nothing captured recently — did you work on any of "
      + "these off the record?";
  section.appendChild(note);

  const entry: QuietEntryCtx | null = ui ? { ui, weeks: inViewWeeks } : null;
  // (e) Same Phase → Category grouping as the main section.
  appendPhaseCategoryGroups(section, items, (it) => renderQuietItem(it, entry));

  // §B Save flushes EVERY dirty week (rows can target any in-view week now).
  if (ui) {
    const bar = document.createElement("div");
    bar.className = "hours-log-save-bar hours-log-noactivity-save";
    const saveBtn = document.createElement("button");
    saveBtn.type = "button";
    saveBtn.className = "hours-log-save-btn";
    saveBtn.textContent = "Save";
    const err = document.createElement("span");
    err.className = "hours-log-save-error";
    err.hidden = true;
    saveBtn.addEventListener("click", async () => {
      saveBtn.disabled = true;
      saveBtn.textContent = "Saving…";
      err.hidden = true;
      try {
        await ui.controller.saveAllDirty();
        saveBtn.textContent = "Saved";
        ui.refreshAll();  // clean → the refresher disables it
      } catch (e) {
        err.hidden = false;
        err.textContent = e instanceof Error ? e.message : String(e);
        saveBtn.textContent = "Save";
        saveBtn.disabled = false;  // re-enable only on failure
      }
    });
    bar.appendChild(saveBtn);
    bar.appendChild(err);
    section.appendChild(bar);
    ui.refreshers.push(() => {
      const dirty = ui.controller.anyDirty();
      saveBtn.disabled = !dirty;
      if (dirty && saveBtn.textContent === "Saved") saveBtn.textContent = "Save";
    });
  }
}

interface QuietEntryCtx { ui: EntryUI; weeks: HoursLogWeek[] }

function renderQuietItem(
  item: HoursLogNoActivityItem, entry: QuietEntryCtx | null = null,
): HTMLElement {
  const row = document.createElement("article");
  row.className = "hours-log-item hours-log-item-quiet";

  const title = document.createElement("div");
  title.className = "hours-log-item-title";
  title.textContent = item.name;   // Category is the group header now — no prefix
  if (entry && entry.weeks.length > 0) {
    row.classList.add("hours-log-item-entry");
    const titleRow = document.createElement("div");
    titleRow.className = "hours-log-item-titlerow";
    titleRow.appendChild(title);
    titleRow.appendChild(makeQuietHoursControl(item, entry));
    row.appendChild(titleRow);
  } else {
    row.appendChild(title);
  }

  // #40c: a REAL last-touched week (extended lookback), not the unreliable
  // week_end-stamped `last_active`. Shown only when a genuine prior-activity
  // week was found; else the item is dormant beyond the lookback and we say so.
  const meta = document.createElement("div");
  meta.className = "hours-log-quiet-meta muted";
  meta.textContent = item.last_touched
    ? `Last activity: week ending ${String(item.last_touched).slice(0, 10)}`
    : "No activity in the last 16 weeks";
  row.appendChild(meta);

  return row;
}

// §B row control (operator #4): a week dropdown (the weeks in view) + an hours
// cell — the "add this to a week" affordance. The item STAYS here after being
// logged so it can be added to more weeks (operator #1). To avoid the confusing
// double-edit surface (2026-07-23): the dropdown defaults to the next week that
// does NOT yet have this item, so §B always reads as "add to a fresh week" —
// the weeks already logged are edited in their own "Also worked" row. Any edit
// made here still propagates: the blur repaints on ANY value change (not only a
// 0↔positive flip), so the week's placed row never shows a stale number.
function makeQuietHoursControl(
  item: HoursLogNoActivityItem, entry: QuietEntryCtx,
): HTMLElement {
  const { ui, weeks } = entry;
  const rowKey = `item:${item.slug}`;
  const wrap = document.createElement("div");
  wrap.className = "hours-log-quiet-control";

  // Default to the first in-view week WITHOUT hours for this item (the next
  // addable week), else the most-recent (all shown weeks already logged). The
  // caller only builds this control when weeks.length > 0.
  const firstUnlogged = weeks.find(
    (w) => (ui.controller.week(w.week_end)?.getHours(rowKey) ?? 0) === 0,
  );
  let target = (firstUnlogged ?? weeks[0]!).week_end;

  const input = document.createElement("input");
  input.className = "hours-log-cell";
  input.type = "text";
  input.inputMode = "decimal";
  input.setAttribute("aria-label", `Hours for ${item.name}`);
  // The target week's hours at last paint — a blur that changed the value
  // repaints so the week's placed row reflects the edit (and §B re-defaults to
  // the next unlogged week).
  let paintedValue = 0;
  const paintValue = (): void => {
    const h = ui.controller.week(target)?.getHours(rowKey) ?? 0;
    input.value = h > 0 ? String(h) : "";
    paintedValue = h;
  };
  paintValue();
  input.addEventListener("input", () => {
    ui.controller.week(target)?.setHours(rowKey, item.name, normalizeHours(input.value));
    ui.refreshAll();
  });
  input.addEventListener("blur", () => {
    const changed = (ui.controller.week(target)?.getHours(rowKey) ?? 0) !== paintedValue;
    paintValue();
    // Any value change → re-render so the item's placed row under the target
    // week appears / disappears / updates, and §B re-defaults to the next
    // unlogged week (the §B list itself keeps the item for further weeks).
    if (changed) ui.repaint();
  });

  if (weeks.length > 1) {
    const select = document.createElement("select");
    select.className = "hours-log-week-select";
    select.setAttribute("aria-label", `Week for ${item.name}`);
    for (const w of weeks) {
      const o = document.createElement("option");
      o.value = w.week_end;
      o.textContent = fmtWeekRange(w.week_start, w.week_end);
      if (w.week_end === target) o.selected = true;
      select.appendChild(o);
    }
    select.addEventListener("change", () => {
      target = select.value;
      paintValue();  // show the newly-selected week's hours for this item
    });
    wrap.appendChild(select);
  }
  wrap.appendChild(input);
  return wrap;
}
